/* eslint-disable no-restricted-syntax */
import { pdf } from '@react-pdf/renderer';

import shortid from 'shortid';
import isURL from 'validator/es/lib/isURL';

import { getExportCancellationReason } from 'shared/constants';
import i18n from 'shared/lib/i18n';

import { SET_MODAL_TYPE } from '../../actionConstants';
import { showProgressModal } from '../Exports/actions';
import { setProgressValue, showInfoModal } from '../Modal/actions';

import { formatDate, getUserTimezone, loadUser } from '../../utils';
import { getFullName } from '../Person/utils';
import { getHighPriorityRoleName } from '../User/utils';

import {
  SAFE_PDF_RECORDS_NUMBER,
  SAFE_PDF_ROWS_PER_DOC_NUMBER,
} from './constants';

import { CONFIRM } from '../Modal/modalTypes';
import { TablePDFTemplate } from './TablePDFTemplate';

async function* fetchData(dispatch, getItems, isLimitedPDF, id, controller) {
  if (dispatch && getItems) {
    let nextItems = { limit: 50, offset: 0 };

    do {
      const response = await getItems({ ...nextItems, controller });

      if (
        typeof response !== 'object' ||
        !response?.data ||
        !Array.isArray(response.data?.results)
      ) {
        throw new Error();
      }

      const { count, next, results } = response.data;
      nextItems = next;
      if (isLimitedPDF && next?.offset >= SAFE_PDF_RECORDS_NUMBER) {
        nextItems = null;
      }
      const progressValue = nextItems?.offset
        ? Math.round((100 / count) * nextItems.offset)
        : 100;

      setProgressValue(dispatch, progressValue, id);
      yield results;
    } while (nextItems);
  }
}

export const getExportData = async (
  dispatch,
  getItems,
  isLimitedPDF,
  hideInfoModal,
  title,
) => {
  const id = shortid.generate();
  const controller = new AbortController();

  const customModalClose = () => {
    controller.abort(getExportCancellationReason().manuallyCanceled);
  };

  showProgressModal(dispatch, true, id, customModalClose, title);

  let hasError = false;
  const values = [];
  try {
    for await (const items of fetchData(
      dispatch,
      getItems,
      isLimitedPDF,
      id,
      controller,
    )) {
      items?.length && values.push(...items);
    }
  } catch (error) {
    values.length = 0;
    hasError = true;
  }

  let message =
    values.length > 0
      ? i18n.t('viewExport.exportCompleted')
      : i18n.t('viewExport.noData');

  if (hasError) {
    message = controller?.signal?.reason
      ? controller.signal.reason
      : getExportCancellationReason().error;
  }

  showProgressModal(dispatch, false);
  !hideInfoModal && !hasError && showInfoModal(dispatch, message);

  return { data: values, isAborted: controller.signal.aborted };
};

export const getItemsCount = async (getItems) => {
  const response = await getItems({ limit: 10, offset: 0 });
  return response?.data?.count;
};

export const confirmLimitedExport = (dispatch, onExport) => {
  const modalProps = {
    bodyText: i18n.t('viewExport.recordsCountNote', {
      count: SAFE_PDF_RECORDS_NUMBER.toLocaleString(),
    }),
    confirmText: i18n.t('viewExport.export'),
    cancelText: i18n.t('actions.cancel'),
    onConfirm: onExport,
  };
  return dispatch({
    type: SET_MODAL_TYPE,
    modalType: CONFIRM,
    modalProps,
  });
};

export const getTablePDFSubtitle = () => {
  const cu = loadUser();
  if (!cu) return null;
  const { pmcRoles = [], properties, isAdmin } = cu;
  const isMasterPm = pmcRoles?.some(({ primary }) => primary === true);

  // get all cu roles ids (unique)
  const allUserRolesIds = [
    ...pmcRoles.map(({ role }) => role).flat(),
    ...properties.map(({ roles }) => roles).flat(),
  ].filter((roleId) => roleId);

  const highPriorityRoleName = getHighPriorityRoleName(allUserRolesIds);

  const userName = getFullName(cu);
  const roleName = isAdmin
    ? i18n.t('roles.admin')
    : isMasterPm
    ? i18n.t('roles.masterPm')
    : highPriorityRoleName;

  const exportTime = formatDate(
    Date.now(),
    true,
    `MM/DD/YY [${i18n.t('metaInfo.at')}] h:mm A`,
  );
  const currentTimezone = getUserTimezone();
  return i18n.t('metaInfo.exportedBy', {
    userName,
    roleName,
    exportTime,
    currentTimezone,
  });
};

export const getExportTitle = (tableTitle, pmc, building) => {
  let propertyName = null;
  if (pmc) {
    propertyName = pmc?.name;
  }
  if (building) {
    propertyName = building?.name;
  }
  return propertyName ? `${propertyName} ${tableTitle}` : tableTitle;
};

export const formatTableData = ({ headers, rows }) => [headers, ...rows];

const getOnePageLimit = () =>
  // TODO add different cases but it's better to have a limit on the API side
  1024;
const onFormatCell = (cell, onePageLimit) => {
  if (!cell || !cell.length) return '';

  if (isURL(cell)) return cell;

  const cellWithNoBreaks = cell?.replace(/[\r\n]{1,}/g, ' ');
  const words = cellWithNoBreaks.split(' ');
  const safeWordLength = 16;

  const safeWords = words
    .map((word) => {
      if (word?.length > safeWordLength) {
        const wordParts = [];
        for (let i = 0; i < word.length; i += safeWordLength) {
          wordParts.push(word.slice(i, i + safeWordLength));
        }
        return wordParts.join(' ');
      }
      return word;
    })
    .join(' ');

  if (safeWords.length > onePageLimit) {
    return safeWords.substring(0, onePageLimit);
  }
  return safeWords;
};

const onFormatRow = (row, onePageLimit) => {
  if (!row || !row.length) return null;
  return row.map((cell) => onFormatCell(cell, onePageLimit));
};

export const onFormatRows = (rows) => {
  if (!rows || !rows.length) return null;
  const onePageLimit = getOnePageLimit(rows);
  return rows.map((row) => onFormatRow(row, onePageLimit));
};

// Sheet name cannot contain : \ / ? * [ ]
export const formatSheetName = (title) =>
  title?.replace(/(:|\\|\/|\?|\*|\[|\])/gm, '-');

export const onSliceTableData = ({ headers, rows }) => {
  const chunks = [];
  const rowsLimit = SAFE_PDF_ROWS_PER_DOC_NUMBER;
  for (let i = 0; i < rows?.length; i += rowsLimit) {
    chunks.push({ headers, rows: rows.slice(i, i + rowsLimit) });
  }
  return chunks;
};

export const createPDF = (tableData, title) => {
  const doc = <TablePDFTemplate title={title} tableData={tableData} />;
  return pdf(doc).toBlob();
};

export const getFileNameSuffix = (i, length) => {
  if (!length || !i || length === 1) return '';
  return i18n.t('metaInfo.partOf', { part: i + 1, partsCount: length });
};

export const onGetDocumentName = (title, extension, suffix = '') => {
  const date = formatDate(Date.now(), true, 'MM-DD-YYYY hh-mm a');
  return `${title} — ${date}${suffix}${extension}`;
};
